import { Button, ConfigProvider, Form } from "antd";
import useForm, { FormInstance } from "antd/es/form/hooks/useForm";
import dayjs, { Dayjs } from "dayjs";
import { debounce } from "lodash";
import { ValidateErrorEntity } from "rc-field-form/lib/interface";
import { ReactElement, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { maxLengthInn } from "../../constants/maxLengthInFolders";
import {
  ruleInn,
  ruleRequired,
} from "../../constants/rules/RulesConstantsForms";
import { useAppDispatch, useAppSelector } from "../../store/reduxHooks";
import { SELECT_USER_DATA } from "../../store/selectors/authSliceLoginSelector";
import {
  ADD_STAGE_IN_REQUEST_CLIENT,
  API_STAGE_INN_REQUEST,
  API_STAGE_PURCHASE_SEARCH_REQUEST,
  API_STAGE_SAVED_APPLICATION,
  CLEAR_CREATE_APLICATION,
  IOnfinishData,
  LotData,
  MATCH_VALUE_RENDERS,
  PORTIAL_DISPLAY_STAGE_PURCHASE,
  PurchaseRequestData,
} from "../../store/slice/applicationCreateSlice/applicationCreateSlice";
import { isMatchRenderNameValueEnum } from "../../store/slice/applicationCreateSlice/types/types";
import { UP_DATA_APPLICATIONS_CREATE } from "../../store/slice/requestAppSlise/requestAppSlise";
import { RootState } from "../../store/Store";
import { USER } from "../../types/indexTypeEnum";
import { UIModuleStikeController } from "../../UI_ITO/indexUI";
import { UI_ITONumericInput } from "../../UI_ITO/UI_ITONumericInput/UI_ITONumericInput";
import { Guard } from "../../utils/guards/Guard";
import { formatDayJsObjectToFormattedDateString } from "./helpers/helpers";
import { InnContainer } from "./innContainer/innContainer";
import { PurchaseContainer } from "./PurchaseContainer/PurchaseContainer";

import classes from "./ApplicationCreate.module.css";

export interface FieldData {
  name: [string];
  value?: unknown;
  touched?: boolean;
  validating?: boolean;
  validated?: boolean;
  errors?: string[];
}

export interface IPurchaseFormFields
  extends Omit<
    IOnfinishData,
    | "start_bg_at"
    | "finish_bg_at"
    | "purchase_total_at"
    | "purchase_end_at"
    | "number_lot"
  > {
  finish_bg_at: Dayjs | string;
  start_bg_at: Dayjs | string;
  purchase_total_at?: Dayjs | string;
  purchase_end_at?: Dayjs | string;
  number_lot: number | string;
}

/**
 * Компонент формы создания заявки.
 */
export function ApplicationCreate(): ReactElement {
  const navigate = useNavigate();
  const [form] = useForm();
  const formRef = useRef<FormInstance<IPurchaseFormFields>>(null);
  const applicationCreateSlice = useSelector(
    (state: RootState) => state.applicationCreateSlice,
  );

  const STAGE_INN_KEY =
    applicationCreateSlice.MATCH_STATE.STAGE_CONTAINER_JSX.CONTAINER_INN;
  const STAGE_PURCHASE_KEY =
    applicationCreateSlice.MATCH_STATE.STAGE_CONTAINER_JSX.CONTAINER_PURCHASE;
  const dispatch = useAppDispatch();
  const user = useAppSelector(SELECT_USER_DATA);

  const purchaseFields: string[] = [
    "ikz",
    "initial_price",
    "name_lot",
    "number_lot",
    "offered_price",
    "sum_avance",
    "sum_bg",
    "sum_bg_execution",
    "inn_customer",
  ];
  const updatePurchaseFields = (
    lotInfo: LotData,
    customerInn: string,
    customerName: string,
  ) => {
    form.setFieldValue(["inn_customer"], customerInn);
    form.setFieldValue(["name_customer"], customerName);
    for (const [key, value] of Object.entries(lotInfo)) {
      if (purchaseFields.find((field) => field === key)) {
        form.setFieldValue([key], value);
      }
    }
  };

  /**
   * @description
   * Достаем данные из массива по информации из селектора
   */
  const handleChangeLotValue = () => {
    const [customerInn, value] = form.getFieldValue("name_lot").split(" //: ");
    const selectedCustomer =
      applicationCreateSlice.PURCHASE_REQUEST.data?.purchase_lot?.find(
        (customerItem) => customerItem.inn_customer === customerInn,
      );
    let selectedLot;
    if (selectedCustomer?.lots)
      selectedLot = selectedCustomer?.lots?.find(
        (lotItem) => lotItem.name_lot === value,
      );
    if (selectedCustomer?.lot) selectedLot = selectedCustomer?.lot;
    if (selectedLot && selectedCustomer) {
      updatePurchaseFields(
        selectedLot,
        selectedCustomer?.inn_customer,
        selectedCustomer?.name_customer,
      );
    }
    form.setFieldValue(["name_lot"], value);
  };
  const getIKZ = (FZ: string) => {
    if (FZ === "сommerce" || FZ === "223") {
      dispatch(
        PORTIAL_DISPLAY_STAGE_PURCHASE({
          STAGE_IKZ: "NOT_DATA",
        }),
      );
    } else if (FZ === "44") {
      dispatch(
        PORTIAL_DISPLAY_STAGE_PURCHASE({
          STAGE_IKZ: "IKZ_REQUIRED",
        }),
      );
    } else {
      dispatch(
        PORTIAL_DISPLAY_STAGE_PURCHASE({
          STAGE_IKZ: "IKZ",
        }),
      );
    }
  };

  useEffect(() => {
    if (user?.organization?.inn && user?.type === USER.CLIENT_SIMPLE) {
      const organization = { success: "succes", data: user.organization };
      dispatch(ADD_STAGE_IN_REQUEST_CLIENT(organization));
    }
  }, [dispatch, user]);

  /* обработка и изменение запроса покупки */
  useEffect(() => {
    form.resetFields(
      purchaseFields.concat([
        "inn_customer",
        "name_customer",
        "purchase_selector",
      ]),
    );
    if (applicationCreateSlice?.PURCHASE_REQUEST?.data) {
      const { data } = applicationCreateSlice.PURCHASE_REQUEST;
      for (const key in data) {
        form.resetFields([key]);
        if (key === "purchase_end_at" || key === "purchase_total_at") {
          const value = data[key as keyof PurchaseRequestData];
          if (typeof value === "string") {
            form.setFieldValue(key, value.substring(0, 10));
          }
        } else if (key === "purchase_lot") {
          const purchase_lot: PurchaseRequestData["purchase_lot"] = data[key];
          for (const purchase_lot_key in purchase_lot) {
            form.resetFields([purchase_lot_key]);
            form.setFieldValue(
              purchase_lot_key,
              purchase_lot[purchase_lot_key as keyof typeof purchase_lot],
            );
          }
        } else {
          form.setFieldValue(key, data[key as keyof PurchaseRequestData]);
        }
      }

      // отображение поля икз в зависимости от подтянутой закупки
      getIKZ(data["fz"]);
    } else {
      //очистка полей формы при ненайденном номере закупки
      const excludeField = [
        "inn",
        "bg_type_id",
        "fz",
        "is_tax_simple",
        "purchase_number",
      ];
      const formValues = form.getFieldsValue();
      Object.keys(formValues)?.forEach((field) => {
        if (!excludeField.includes(field)) {
          form.resetFields([field]);
        }
      });
    }
  }, [applicationCreateSlice.PURCHASE_REQUEST, form]);

  const { SWAP_PAGE_REQUEST_CREATE, STAGE_INN_REQUEST } =
    applicationCreateSlice;

  useEffect(() => {
    let action = true;
    if (action) {
      if (SWAP_PAGE_REQUEST_CREATE) {
        const id =
          applicationCreateSlice.SWAP_PAGE_REQUEST_CREATE_DATA?.data.id;
        const responseData =
          applicationCreateSlice.SWAP_PAGE_REQUEST_CREATE_DATA
            ? applicationCreateSlice.SWAP_PAGE_REQUEST_CREATE_DATA
            : { data: false };
        if (responseData) {
          dispatch(UP_DATA_APPLICATIONS_CREATE(responseData));
        }
        navigate("/application" + "/" + id);
      }
    }
    return () => {
      action = false;
      dispatch(CLEAR_CREATE_APLICATION("clear"));
      form.resetFields();
    };
  }, [
    SWAP_PAGE_REQUEST_CREATE,
    applicationCreateSlice.SWAP_PAGE_REQUEST_CREATE_DATA,
    applicationCreateSlice.SWAP_PAGE_REQUEST_CREATE_DATA?.data.id,
    dispatch,
    navigate,
  ]);
  /* при нажатии на кнопку Создать заявку и сохранить документы */
  const onFinish = (value: IPurchaseFormFields) => {
    const start_bg_at = formatDayJsObjectToFormattedDateString(
      value.start_bg_at,
    );
    const finish_bg_at = formatDayJsObjectToFormattedDateString(
      value.finish_bg_at,
    );
    const purchase_total_at = value?.purchase_total_at
      ? formatDayJsObjectToFormattedDateString(value.purchase_total_at)
      : undefined;
    const purchase_end_at = value?.purchase_end_at
      ? formatDayJsObjectToFormattedDateString(value.purchase_end_at)
      : undefined;

    const number_lot = Number(value?.number_lot);

    if (!value.inn && user.inn) {
      value.inn = user.inn;
    }

    // TODO: замокал ибо бэк не валидирует без этого ключа
    value.banks = [];

    dispatch(
      API_STAGE_SAVED_APPLICATION({
        ...value,
        total_sum: form.getFieldValue("total_sum"),
        initial_price: form.getFieldValue("initial_price"),
        sum_avance: form.getFieldValue("sum_avance"),
        sum_bg_execution: form.getFieldValue("sum_bg_execution"),
        sum_bg_obligation: form.getFieldValue("sum_bg_obligation"),
        sum_bg: form.getFieldValue("sum_bg"),
        start_bg_at,
        finish_bg_at,
        purchase_total_at,
        purchase_end_at,
        number_lot,
      }),
    );
  };

  const onFinishFailed = (
    errorInfo: ValidateErrorEntity<IPurchaseFormFields>,
  ) => {
    const failedField = errorInfo.errorFields[0]?.name;
    const fieldElement = document.getElementById(`Aplication_${failedField}`);
    // скролл на ошибку
    if (fieldElement) {
      fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    // фокус на поле с ошибкой
    if (failedField && formRef.current) {
      const fieldInstance = formRef.current.getFieldInstance(failedField);
      if (fieldInstance) {
        fieldInstance.focus();
      }
    }
  };

  const debouncedApiStageInnRequest = debounce((value) => {
    dispatch(API_STAGE_INN_REQUEST(value));
  }, 500);

  /* Поиск закупки (при печати номера) с дебаунсом */
  const debouncedApiStagePurchaseSearch = debounce((value) => {
    dispatch(API_STAGE_PURCHASE_SEARCH_REQUEST(value));
  }, 500);

  const onFieldsChange = (values: FieldData[]) => {
    const FZ = form.getFieldValue("fz");
    const {
      value,
      name: [nameValue],
      validated,
    } = values[0];

    // поставил условие так как в данном редьюсере используется только три типа строка,
    // без этого условия выполняется функция диспатч c ненужными по факту значениями namevalue
    // вследствии чего в редакс попадали несериализуемые данные и тот выдавал ошибку в консоли
    if (isMatchRenderNameValueEnum(nameValue))
      dispatch(MATCH_VALUE_RENDERS({ nameValue, value }));

    // Отправлять запрос только когда меняем инн
    if (values.length === 1 && validated && nameValue === "inn") {
      if (typeof value === "string") {
        if (value.length === 10 || value.length === 12) {
          debouncedApiStageInnRequest(value);
        }
      }
    }
    // Отправлять запрос только когда меняем номер закупки
    if (values.length === 1 && validated && nameValue === "purchase_number") {
      if (typeof value === "string") {
        if (value.length === 11 || value.length === 18 || value.length === 19) {
          debouncedApiStagePurchaseSearch(value);
        } else if (value.length === 0) {
          form.setFieldValue("purchase_number", undefined);
        }
      }
    }
    if (
      typeof value === "string" &&
      nameValue === "bg_type_id" &&
      value === "5"
    ) {
      form.setFieldValue("fz", "commerce");
    }

    if (nameValue === "purchase_total_at" && typeof value === "string") {
      form.setFieldValue("purchase_total_at", value ? dayjs(value) : "");
    }

    getIKZ(FZ);
  };
  const ConstructorJsx = {
    STAGE_CONTAINER_JSX: {
      CONTAINER_INN: {
        INN_GOOD: <InnContainer />,
        NOT_DATA: null,
      },

      CONTAINER_PURCHASE: {
        PURCHASE_GOOD: (
          <PurchaseContainer
            className={classes.applicationCreation__formSection}
            handleChangeLotValue={handleChangeLotValue}
          />
        ),
        NOT_DATA: null,
      },
      CONTAINER_CONTRACTP_RICE: {
        CONTRACTP_RICE_GOOD: <>CONTRACTP_RICE_GOOD</>,
        NOT_DATA: null,
      },
    },
  };

  const FORM_ID = "Aplication";

  return (
    <>
      <ConfigProvider
        input={{ className: classes.applicationCreation__input }}
        inputNumber={{ className: classes.applicationCreation__input }}
        select={{ className: classes.applicationCreation__select }}
        checkbox={{ className: classes.applicationCreation__checkbox }}
        datePicker={{ className: classes.applicationCreation__datePicker }}
      >
        <Form
          ref={formRef}
          onFinish={onFinish}
          layout="vertical"
          name={FORM_ID}
          form={form}
          initialValues={{ ikz: "", smp: false }}
          className={classes.applicationCreation__form}
          onFieldsChange={onFieldsChange}
          onFinishFailed={onFinishFailed}
        >
          <Guard.Role roles={[USER.AGENT_SIMPLE, USER.SUPPORT_CREDIT]}>
            <div className={classes.applicationCreation__formSection}>
              <h2 className={classes.applicationCreation__title}>Клиент</h2>
              <Form.Item
                name="inn"
                label="ИНН"
                required={true}
                rules={[ruleRequired, ruleInn]}
                className={classes.applicationCreation__formItem}
              >
                <UI_ITONumericInput
                  size="large"
                  maxLength={maxLengthInn}
                  placeholder="Введите ИНН"
                />
              </Form.Item>
              {ConstructorJsx.STAGE_CONTAINER_JSX.CONTAINER_INN[STAGE_INN_KEY]}
            </div>
          </Guard.Role>

          <Guard.Role roles={[USER.CLIENT_SIMPLE]}>
            {ConstructorJsx.STAGE_CONTAINER_JSX.CONTAINER_INN[STAGE_INN_KEY]}
          </Guard.Role>

          {
            ConstructorJsx.STAGE_CONTAINER_JSX.CONTAINER_PURCHASE[
              STAGE_PURCHASE_KEY
            ]
          }
        </Form>
      </ConfigProvider>
      <UIModuleStikeController>
        <Button
          form={FORM_ID}
          type="primary"
          htmlType="submit"
          disabled={!STAGE_INN_REQUEST.success}
          className={classes.applicationCreation__submitButton}
        >
          Создать заявку
        </Button>
      </UIModuleStikeController>
    </>
  );
}
// {
//   "bg_type_id" // тип банковской гарантии
//   "organization_id" // id организации
//   "is_tax_simple" // система налогообложения
//   "total_sum" // Итоговая сумма заявки на банковскую гарантию
//   "inn" // инн принципала, который подает заявку на банковскую гарантию,
//   "start_bg_at" // 2023-09-19 00:00:00 -> 19.09.2023 ,
//   "finish_bg_at",
//   "link": // ссылка на закупку,
//   "purchase_number" // номер закупки,
//   "fz" // fz 44 fz 185 fz 615 fz сommerce,
//   "subject_contract"
//   "is_closed" // закрытый аукцион ли,
//   "is_reprovision" // переобеспечение ли,
//   "is_sole_supplier" // единственный поставщик ли,
//   "is_indisputable": // есть ли требование о бесспорном списании средств,
//
//   "ikz",
//   "initial_price" // Сумма обеспечения начальная максимальная цена,
//   "inn_customer",
//   "name_customer",
//   "name_lot",
//   "sum_bg_execution" // Сумма обеспечения сумма (размер) обеспечения на исполнение (контракт),
//   "sum_bg" // Сумма обеспечения сумма
//   "number_lot"
//   "link" // ссылка на тендер закупки
//
// }
